import { Box, Button, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { useRootContext } from 'components/RootContainer';
import { useFeedback } from 'hooks/useFeedback';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { REPORT_PATH } from '../../../../../../@constants/Navigation/AppPaths/RootPaths';
import EmptyContent from '../../../../../../components/EmptyContent';
import ModalLoading from '../../../../../../components/ModalLoading';
import { ArrowLeft2Icon } from '../../../../../../components/SvgIcons/ArrowLeft2Icon';
import { Bookmark2Icon } from '../../../../../../components/SvgIcons/Bookmark2Icon';
import { ExportErrorFeedback } from '../../../../helpers/ExportErrorFeedback';
import { DailyReportRequestModel } from './api/models/DailyReportPageRequestModel';
import { FilterFormProvider } from './components/FilterForm/FilterFormProvider';
import { getDefaultValues } from './components/FilterForm/defaultValues';
import { DailyReportFormModel } from './models';
import { dataFormToRequest } from './utils/dataFormToRequest';

interface CommentResponse {
  comment: string;
}
const DailyReportPage: FC = () => {
  const { onSuccess, onError, closeSnackbar } = useFeedback();
  const {
    globalSettings,
    HotelApi: {
      report: {
        frontDeskReport: { dailyReport },
      },
    },
  } = useRootContext();

  const form = useForm<DailyReportFormModel>({
    defaultValues: {
      ...getDefaultValues(globalSettings!),
      comment: '',
    },
  });

  useEffect(() => {
    if (globalSettings?.frontend_date) {
      form.reset({
        day: globalSettings?.frontend_date,
        comment: form.getValues('comment') || '',
      });
    }
  }, [globalSettings?.frontend_date]);

  const currentDate = form.watch('day');

  // Query to fetch existing comment
  useQuery<CommentResponse>({
    queryKey: ['daily-report-comment', currentDate || globalSettings?.frontend_date],
    queryFn: () => {
      const date = currentDate || globalSettings?.frontend_date;
      if (!date) {
        throw new Error('No date selected');
      }
      return dailyReport.getComment({ date });
    },
    enabled: !!(currentDate || globalSettings?.frontend_date),
    onSuccess: data => {
      form.reset({
        ...form.getValues(),
        day: currentDate || globalSettings?.frontend_date!,
        comment: data?.comment || '',
      });
    },
    onError: error => {
      console.error('Failed to fetch comment:', error);
      onError({
        snackbar: {
          title: 'Error',
          message: 'Failed to fetch comment',
        },
      });
    },
  });

  useEffect(() => {
    if (globalSettings?.frontend_date) {
      form.reset({
        day: globalSettings?.frontend_date,
        comment: form.getValues('comment') || '',
      });
    }
  }, [globalSettings?.frontend_date]);

  const saveCommentMutation = useMutation<any, Error, DailyReportRequestModel>({
    mutationKey: ['save-daily-report-comment'],
    mutationFn: data => dailyReport.saveComment(data),
    onSuccess: () => {
      onSuccess({
        snackbar: {
          title: 'Success',
          message: 'Comment saved successfully',
        },
      });
    },
    onError: error => {
      console.error('Failed to save comment:', error);
      onError({
        snackbar: {
          title: 'Error',
          message: 'Failed to save comment',
        },
      });
    },
  });

  const exportPDF = useMutation({
    mutationKey: ['exportPDF'],
    mutationFn: (payload: DailyReportRequestModel) => {
      return dailyReport.exportPDF(payload);
    },
    onError: (error: AxiosError, variables) => {
      const content = (
        <ExportErrorFeedback
          status={error.response?.status}
          message={(error as { message?: string }).message}
          onExportCsv={() => exportPDF.mutate({ ...variables, file_type: 'csv' })}
          onClose={closeSnackbar}
          recommendedFormat="csv"
        />
      );

      onError({
        snackbar: {
          title: 'Error exporting report',
          message: content,
        },
        options: { persist: false },
        autoHideDuration: 5000,
      });
    },
  });

  const handleSave = (data: DailyReportFormModel) => {
    const requestData = dataFormToRequest(data);

    saveCommentMutation.mutate(requestData);
  };

  const handleSaveAndExport = (data: DailyReportFormModel) => {
    const requestData = dataFormToRequest(data);

    exportPDF.mutate(requestData);
  };

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(REPORT_PATH);
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
        }}
      >
        <Box
          sx={{
            width: '24rem',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'colors.gray.o10',
          }}
        >
          <Button
            startIcon={<ArrowLeft2Icon />}
            sx={{
              fontSize: '1rem',
              fontWeight: 'fontWeightMedium',
              justifyContent: 'flex-start',
              m: '1rem',
            }}
            onClick={handleBackClick}
          >
            Back
          </Button>
          <FilterFormProvider
            form={form}
            onSubmit={data => handleSaveAndExport(data)}
            onSave={data => handleSave(data)}
            onSaveAndExport={data => handleSaveAndExport(data)}
            isSaving={saveCommentMutation.isLoading}
            isExporting={exportPDF.isLoading}
            defaultValues={{
              ...getDefaultValues(globalSettings!),
              comment: '',
            }}
          />
        </Box>
        <Stack
          position="relative"
          flexGrow={1}
          height="100%"
          sx={{
            p: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
            <ModalLoading open={exportPDF.isLoading} title="Exporting..." />
            <EmptyContent
              IconProps={<Bookmark2Icon />}
              title="Reports"
              subTitle="Select date and generate report"
            />
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default DailyReportPage;
